// Phase 1 DEMO — mock data only, no backend.
// Curated, dated RBI Bank Rate table (manual curation, Section 15/16 MSMED Act).
// Statutory interest = 3 × RBI Bank Rate, compounded with monthly rests.

export const RBI_BANK_RATE = [
  { from: "2023-02-08", rate: 6.75 },
  { from: "2024-02-08", rate: 6.75 },
  { from: "2025-04-09", rate: 6.25 },
  { from: "2025-06-06", rate: 6.00 },
];

// Demo invoices (mock)
export const INVOICES = [
  {
    id: "INV-2024-0417",
    buyer: "Meridian Auto Components Pvt Ltd",
    buyerType: "Large Enterprise",
    invoiceDate: "2024-11-12",
    acceptanceDate: "2024-11-15",
    creditPeriod: 30,
    principal: 450000,
    status: "Demand Notice",
    stageIndex: 2,
    evidence: {
      po: true, invoice: true, deliveryChallan: true,
      emailConfirm: true, gstr1: true, statement: false,
    },
  },
  {
    id: "INV-2024-0388",
    buyer: "Sunrise Retail Distributors",
    buyerType: "Medium Enterprise",
    invoiceDate: "2024-12-02",
    acceptanceDate: "2024-12-05",
    creditPeriod: 45,
    principal: 128500,
    status: "Reminder",
    stageIndex: 1,
    evidence: {
      po: true, invoice: true, deliveryChallan: false,
      emailConfirm: true, gstr1: true, statement: false,
    },
  },
  {
    id: "INV-2025-0021",
    buyer: "Bluewave Infrastructure Ltd",
    buyerType: "Large Enterprise",
    invoiceDate: "2025-01-18",
    acceptanceDate: "2025-01-20",
    creditPeriod: 45,
    principal: 890000,
    status: "Samadhaan Filing",
    stageIndex: 3,
    evidence: {
      po: true, invoice: true, deliveryChallan: true,
      emailConfirm: true, gstr1: true, statement: true,
    },
  },
];

export const EVIDENCE_LABELS = {
  po: "Purchase Order",
  invoice: "Tax Invoice",
  deliveryChallan: "Delivery Challan / LR",
  emailConfirm: "Email / WhatsApp Confirmation",
  gstr1: "GSTR-1 Filing Reference",
  statement: "Ledger / Account Statement",
};

export const EVIDENCE_WEIGHTS = {
  po: 20, invoice: 20, deliveryChallan: 20,
  emailConfirm: 15, gstr1: 15, statement: 10,
};

export const CASE_STAGES = [
  { key: "intake", label: "Intake", desc: "Invoice & evidence captured" },
  { key: "reminder", label: "Reminder", desc: "Payment reminder issued to buyer" },
  { key: "notice", label: "Demand Notice", desc: "Statutory notice u/s 16 served" },
  { key: "samadhaan", label: "Samadhaan Filing", desc: "Reference filed with MSEFC" },
  { key: "conciliation", label: "Conciliation", desc: "MSEFC conciliation proceedings" },
  { key: "arbitration", label: "Arbitration", desc: "Referred to arbitration u/s 18" },
  { key: "recovered", label: "Recovered / Closed", desc: "Dues + interest recovered" },
];

const rateOn = (dateStr) => {
  const d = new Date(dateStr).getTime();
  let r = RBI_BANK_RATE[0].rate;
  for (const row of RBI_BANK_RATE) {
    if (new Date(row.from).getTime() <= d) r = row.rate;
  }
  return r;
};

// Compute statutory interest with monthly rests from the "due date"
// (acceptance date + credit period, capped at 45 days per statute).
export function computeInterest(invoice, asOf = "2025-06-15") {
  const cap = Math.min(invoice.creditPeriod, 45);
  const accept = new Date(invoice.acceptanceDate);
  const due = new Date(accept.getTime() + cap * 86400000);
  const end = new Date(asOf);

  const rows = [];
  let principalBase = invoice.principal;
  let cursor = new Date(due);

  // iterate month by month (monthly rests)
  let safety = 0;
  while (cursor < end && safety < 120) {
    safety++;
    const next = new Date(cursor);
    next.setMonth(next.getMonth() + 1);
    const periodEnd = next < end ? next : end;
    const days = Math.round((periodEnd - cursor) / 86400000);
    if (days <= 0) break;
    const bankRate = rateOn(cursor.toISOString().slice(0, 10));
    const statRate = bankRate * 3;
    const interest = (principalBase * (statRate / 100) * days) / 365;
    rows.push({
      from: cursor.toISOString().slice(0, 10),
      to: periodEnd.toISOString().slice(0, 10),
      days,
      bankRate,
      statRate,
      base: Math.round(principalBase),
      interest: Math.round(interest),
    });
    principalBase += interest; // monthly rest — interest capitalised
    cursor = periodEnd;
  }

  const totalInterest = rows.reduce((s, r) => s + r.interest, 0);
  const daysOverdue = Math.max(0, Math.round((end - due) / 86400000));
  return {
    due: due.toISOString().slice(0, 10),
    daysOverdue,
    rows,
    totalInterest: Math.round(totalInterest),
    grandTotal: Math.round(invoice.principal + totalInterest),
  };
}

export function evidenceScore(evidence) {
  let score = 0;
  for (const k of Object.keys(EVIDENCE_WEIGHTS)) {
    if (evidence[k]) score += EVIDENCE_WEIGHTS[k];
  }
  return score;
}
